import { useMemo } from 'react';
import { usePlasmicCMS } from '~/App/Api/Plasmic';
import { GuideProgressType, Service } from '~/types/models';
import {
  OnboardingSortOrder,
  PlasmicModelType,
  OnboardingPanel,
} from '~/types/models/plasmic';

export interface UseMappedGuideProgressArgs {
  service: Service;
  completedGuides?: GuideProgressType[];
}

export interface UseMappedGuideProgressResult {
  panels: OnboardingPanel[];
  loading: boolean;
  total: number;
  progress: number;
  isCompleted: boolean;
}

// Returns the Consolidated the Sorted panels, Guide progress, and loading state
export function useMappedGuideProgress({
  completedGuides,
  service,
}: UseMappedGuideProgressArgs) {
  const { data: onboardingSortOrder, isLoading: isOnboardingSortOrderLoading } =
    usePlasmicCMS<OnboardingSortOrder>({
      model: PlasmicModelType.OnboardingSortOrder,
      keys: { service },
      query: { service },
    });

  const { data: onboardingPanels, isLoading: isOnboardingPanelLoading } =
    usePlasmicCMS<OnboardingPanel>({
      model: PlasmicModelType.OnboardingPanel,
      keys: { service },
      query: { service },
    });

  const loading = isOnboardingSortOrderLoading || isOnboardingPanelLoading;

  const { sortedPanels, nonDependentPanels, progress } = useMemo(() => {
    if (!onboardingSortOrder || !onboardingPanels || !completedGuides)
      return { sortedPanels: [], nonDependentPanels: [], progress: 0 };

    // Iterate the sortOrder and fetch the respective panels based on the sort order
    const sortedPanels = onboardingSortOrder[0]?.sortOrder
      ?.map((sortOrder) => {
        const dependencies =
          sortOrder?.dependencies?.map((i) => i['guideProgressType']) ?? [];

        // If there are no dependencies OR if dependency progress are already completed, render the panel
        if (
          dependencies.length === 0 ||
          dependencies.every((dependency) =>
            completedGuides.includes(dependency)
          )
        ) {
          return {
            ...onboardingPanels.filter(
              (panel) => panel.guideProgressType === sortOrder.guideProgressType
            )[0],
            dependencies,
          } as OnboardingPanel;
        }
      })
      .filter((panel): panel is OnboardingPanel => panel !== undefined);

    const nonDependentPanels = sortedPanels?.filter(
      (panel) => panel.dependencies?.length === 0
    );

    // Counts the non-dependent panel progress base on the shown sorted panels and the completed guides
    const progress =
      nonDependentPanels?.filter((panel) =>
        completedGuides.includes(panel?.guideProgressType)
      ).length ?? 0;

    return { sortedPanels, nonDependentPanels, progress };
  }, [onboardingPanels, onboardingSortOrder, completedGuides]);

  return {
    panels: sortedPanels,
    loading,
    total: nonDependentPanels?.length,
    progress,
    isCompleted: nonDependentPanels?.length === progress,
  } as UseMappedGuideProgressResult;
}
